import {isEnterprise} from '@github-ui/runtime-environment'

export const LABELS = {
  mutationErrorMessage: 'Something went wrong while setting the field value. Please try again.',
  loadingFieldsTooltipMessage: "Couldn't load issue fields",
  loadingFieldsErrorMessage:
    "We couldn't load the issue fields. Please try again later or if the problem persists contact support",
  setValue: 'None yet',
  emptyValueLabels: {
    text: 'Enter text…',
    number: 'Enter number…',
    date: 'Choose a date',
    singleSelect: 'Choose an option',
    multiSelect: 'Choose options',
  },
  reloadWarningMessage: 'Reload site? Changes you made might not be saved.',

  textValidationMaxLength: (length: number, maxLength: number) =>
    `Value cannot be more than ${maxLength} characters (${length})`,
  textValidationEmpty: 'Text cannot be empty.',
  textEditorLabel: (fieldName: string) => `The value for the ${fieldName} field`,
  textEditorToolbarLabel: (fieldName: string) => `Toolbar for ${fieldName} field`,
  textEditorPlaceholder: 'Enter text…',
  numberEditorPlaceholder: 'Enter number…',
  numberValidation: {
    errorMessage: 'Must contain only numbers, decimals, and optional negative sign',
    max: (max: number) => `Cannot be greater than ${max}`,
    min: (min: number) => `Cannot be less than ${min}`,
  },
  textEditorUpdateButton: 'Update',
  textEditorRemoveFieldButtonLabel: (fieldName: string) => `Remove field ${fieldName}`,
  textEditorUpdateButtonAriaLabel: (fieldName: string) => `Update field ${fieldName} (press enter to submit)`,
  textEditorCancelButton: 'Cancel',
  errorUpdatingIssueField: 'There was an error updating this value. Please try again.',
  editFieldValueLabel: (title: string) => `Edit ${title}`,
  noPinnedFieldsMessage: (issueTypeName?: string | null) =>
    issueTypeName ? `No fields configured for ${issueTypeName}.` : 'No fields configured for issues without a type.',
  visibilityIndicatorTooltip: 'Field is visible to the public.',
  visibilityIndicatorEnterpriseTooltip: 'Field is visible to all organizations in this enterprise.',
}

/**
 * On managed environments (GHES, Proxima/GHE.com) and EMU organizations, repositories are never
 * truly public, so the "visible to the public" wording is misleading.
 *
 * - `isEnterprise()` is true whenever the runtime-environment is `enterprise` (e.g. GHES).
 * - `isOwnerEnterpriseManaged` is true for EMU organizations. This also covers Proxima/GHE.com:
 *   every customer organization there belongs to an enterprise-managed business, so the EMU flag
 *   is true even though `isEnterprise()` is false.
 */
export function getVisibilityIndicatorTooltip(isOwnerEnterpriseManaged?: boolean): string {
  return isEnterprise() || isOwnerEnterpriseManaged
    ? LABELS.visibilityIndicatorEnterpriseTooltip
    : LABELS.visibilityIndicatorTooltip
}

/**
 * - `PUBLIC` repos always expose the value, so the indicator shows everywhere.
 * - `INTERNAL` repos expose the value to every organization in the enterprise. We only surface the
 *   indicator on managed environments (`isEnterprise()`) or EMU owners (`isOwnerEnterpriseManaged`),
 *   where the copy reads "Internal". On plain dotcom the copy still reads "Public", so a globe on an
 *   internal repo would be misleading and is suppressed.
 * - `PRIVATE` repos never expose the value, so the indicator is always suppressed.
 */
export function shouldShowFieldVisibilityIndicators(
  visibility: string | null | undefined,
  isOwnerEnterpriseManaged?: boolean | null,
): boolean {
  if (visibility === 'PUBLIC') return true
  if (visibility === 'INTERNAL') return isEnterprise() || Boolean(isOwnerEnterpriseManaged)
  return false
}
